import {useMemo, useState, type MouseEvent as ReactMouseEvent} from 'react';
import {alpha, styled} from '@mui/material/styles';
import {SimpleTreeView, TreeItem} from '@mui/x-tree-view';
import {type NavigationTreeCage, type NavigationTreePlan} from 'client';
import {MinusSquare, PlusSquare} from 'assets/icons';
import {Checkbox, Typography, InputBase, InputAdornment} from '@mui/material';
import {useTranslation} from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';

export const AssignTree = ({
  planTreeNavigation,
  selected,
  setSelected,
  dataTestIdPrefix = '',
}: {
  selected: TreeSelection;
  setSelected: (selection: TreeSelection) => void;
  planTreeNavigation: NavigationTreePlan;
  dataTestIdPrefix?: string;
}) => {
  const {t} = useTranslation('common');
  const [searchValue, setSearchValue] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const search = searchValue.toLowerCase().trim();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearchValue(newSearchValue);

    if (!newSearchValue) {
      setExpandedItems([]);
      return;
    }

    const search = newSearchValue.toLowerCase().trim();
    const itemsToExpand =
      planTreeNavigation.sites
        ?.filter((site) => site.name.toLowerCase().includes(search))
        .map((site) => `site${site.id}`) ?? [];

    setExpandedItems(itemsToExpand);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && filteredPlanTree && filteredPlanTree.length > 0) {
      const firstSite = filteredPlanTree[0];
      const selection = treeAssignerHandler({
        id: firstSite.id,
        siteId: firstSite.id,
        type: 'site',
        selected,
        planTreeNavigation,
        siteCagesMap,
      });
      setSelected(selection);
    }
  };

  const filteredPlanTree = search
    ? planTreeNavigation?.sites?.filter((site) => site.name.toLowerCase().includes(search))
    : planTreeNavigation.sites;

  const siteCagesMap = useMemo(() => {
    return (planTreeNavigation.sites ?? []).reduce((map, site) => {
      map.set(site.id, site.cages ?? []);
      return map;
    }, new Map<string, NavigationTreeCage[]>());
  }, [planTreeNavigation.sites]);

  return (
    <>
      <InputBase
        fullWidth
        value={searchValue}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        placeholder={t('search')}
        autoFocus
        sx={{mb: 1, px: 2}}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="inherit" color="secondary" />
          </InputAdornment>
        }
      />
      <StyledTreeView
        multiSelect
        expandedItems={expandedItems}
        onExpandedItemsChange={(_, itemIds) => {
          setExpandedItems(itemIds);
        }}
        slots={{
          expandIcon: PlusSquare,
          collapseIcon: MinusSquare,
        }}
      >
        {filteredPlanTree?.length ? (
          filteredPlanTree?.map((site) => {
            const isSiteSelected = selected.siteIds.includes(site.id);
            const indeterminate = site.cages?.some((cage) => selected.cageIds.includes(cage.id));
            return (
              <StyledTreeItem
                key={site.id}
                itemId={`site${site.id}`}
                data-testid={`${dataTestIdPrefix}-site-${site.id}`}
                label={
                  <CheckboxLabel
                    id={site.id}
                    label={site.name}
                    checked={isSiteSelected}
                    indeterminate={indeterminate}
                    onClick={(event) => {
                      event.stopPropagation();
                      const selection = treeAssignerHandler({
                        id: site.id,
                        siteId: site.id,
                        type: 'site',
                        selected,
                        planTreeNavigation,
                        siteCagesMap,
                      });
                      setSelected(selection);
                    }}
                    dataTestId={`${dataTestIdPrefix}-site-${site.id}-checkbox`}
                  />
                }
              >
                {site.cages &&
                  site.cages.map((cage) => {
                    const isCageSelected = selected.cageIds.includes(cage.id);
                    return (
                      <StyledTreeItem
                        key={cage.id}
                        itemId={`cage${cage.id}`}
                        data-testid={`${dataTestIdPrefix}-cage-${cage.id}`}
                        label={
                          <CheckboxLabel
                            id={cage.id}
                            label={cage.name}
                            checked={isCageSelected || isSiteSelected}
                            onClick={(event) => {
                              event.stopPropagation();
                              const selection = treeAssignerHandler({
                                id: cage.id,
                                siteId: site.id,
                                type: 'cage',
                                selected,
                                planTreeNavigation,
                                siteCagesMap,
                              });
                              setSelected(selection);
                            }}
                            dataTestId={`${dataTestIdPrefix}-cage-${cage.id}-checkbox`}
                          />
                        }
                      />
                    );
                  })}
              </StyledTreeItem>
            );
          })
        ) : (
          <Typography variant="caption" color="error.main">
            {t('noMatch')}
          </Typography>
        )}
      </StyledTreeView>
    </>
  );
};

const CheckboxLabel = ({
  id,
  label,
  checked,
  indeterminate,
  onClick,
  dataTestId,
}: {
  id: string;
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onClick: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
  dataTestId?: string;
}) => {
  return (
    <span>
      <StyledCheckbox
        checked={checked}
        indeterminate={(!checked && indeterminate) ?? false}
        onClick={onClick}
        data-testid={dataTestId}
      />
      {label}
    </span>
  );
};

const StyledTreeItem = styled(TreeItem)(({theme}) => ({
  iconContainer: {
    padding: theme.spacing(1),
    '& .close': {
      opacity: 0.3,
    },
  },
  group: {
    marginLeft: 7,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
  label: {
    fontSize: '14px',
    borderRadius: '100px',
    '&:hover': {
      borderRadius: '100px',
    },
  },
}));

const StyledTreeView = styled(SimpleTreeView)(({theme}) => ({
  flexGrow: 1,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
  fontSize: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
}));

const StyledCheckbox = styled(Checkbox)(({theme}) => ({
  padding: '2px',
  marginRight: '4px',
  color: `${theme.palette.primary.main} !important`,
  '&$checked': {
    color: theme.palette.primary.main,
  },
}));
export interface TreeSelection {
  siteIds: string[];
  cageIds: string[];
}

const selectionHandler = (selected: string[], id: string) => {
  if (selected.includes(id)) {
    return selected.filter((selection) => selection !== id);
  } else {
    return selected.concat(id);
  }
};

const treeAssignerHandler = ({
  id,
  siteId,
  type,
  selected,
  planTreeNavigation,
  siteCagesMap,
}: {
  id: string;
  siteId?: string;
  type: 'site' | 'cage';
  selected: TreeSelection;
  planTreeNavigation: NavigationTreePlan;
  siteCagesMap: Map<string, NavigationTreeCage[]>;
}): TreeSelection => {
  if (type === 'site') {
    const newSiteSelection = selectionHandler(selected.siteIds, id);
    const cageSelectionSet = new Set(selected.cageIds);
    const siteCages = siteCagesMap.get(id) ?? [];

    siteCages.forEach((cage) => {
      newSiteSelection.includes(id) ? cageSelectionSet.add(cage.id) : cageSelectionSet.delete(cage.id);
    });

    return {
      siteIds: newSiteSelection,
      cageIds: Array.from(cageSelectionSet),
    };
  } else if (type === 'cage') {
    const selectedSites = selected.siteIds;
    const selectedCages = selectionHandler(selected.cageIds, id);
    const siteCagesSelected = planTreeNavigation.sites?.find((site) => site.id === siteId)?.cages;
    const allCagesSelectedNot =
      siteCagesSelected && siteCagesSelected.map((cage) => selectedCages.includes(cage.id)).includes(false);

    const uniqueSelectedSites = () => {
      if (!allCagesSelectedNot && siteId) {
        if (selectedSites.includes(siteId)) {
          return selectedSites;
        } else {
          return selectedSites.concat(siteId);
        }
      } else {
        return selectedSites.filter((id) => siteId !== id);
      }
    };

    return {
      siteIds: uniqueSelectedSites(),
      cageIds: selectedCages,
    };
  } else {
    throw new Error(`${type} not handled`);
  }
};
